import React, { useState, useEffect } from 'react';
import { FiAward, FiTrendingUp, FiUsers, FiTarget } from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { parentAnalyticsAPI } from '../../../api/parentEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Section,
  SectionTitle,
  Grid,
  Card,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Badge,
  RankingCard,
  RankBadge,
  RankInfo,
  ChartContainer,
  LoadingContainer,
  EmptyState,
} from '../Analytics.styles';

const ComparativeAnalytics = ({ studentId }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await parentAnalyticsAPI.getComparativeAnalytics(studentId);
      setData(response.data.comparative_analytics);
    } catch (error) {
      showToast('error', 'Failed to load comparative analytics');
      console.error('Error fetching comparative analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPercentileColor = (percentile) => {
    if (percentile >= 90) return '#FFD700'; // Gold
    if (percentile >= 75) return '#C0C0C0'; // Silver
    if (percentile >= 50) return '#CD7F32'; // Bronze
    return theme.colors.primary;
  };

  const getPercentileBadge = (percentile) => {
    if (percentile >= 90) return { emoji: '🥇', label: 'Top 10%', color: '#FFD700' };
    if (percentile >= 75) return { emoji: '🥈', label: 'Top 25%', color: '#C0C0C0' };
    if (percentile >= 50) return { emoji: '🥉', label: 'Top 50%', color: '#CD7F32' };
    return { emoji: '📊', label: 'Average', color: theme.colors.primary };
  };

  const getPerformanceColor = (performance) => {
    if (performance === 'above') return theme.colors.success;
    if (performance === 'below') return theme.colors.error;
    return theme.colors.textSecondary;
  };

  const prepareSubjectComparison = () => {
    if (!data || !data.subject_rankings) return [];
    
    return data.subject_rankings.map((subject) => ({
      subject: subject.subject.length > 15 
        ? subject.subject.substring(0, 12) + '...' 
        : subject.subject,
      studentScore: subject.student_average,
      gradeAverage: subject.grade_average,
      difference: subject.difference,
    }));
  };

  const prepareRadarData = () => {
    if (!data || !data.subject_rankings) return [];
    
    return data.subject_rankings.slice(0, 6).map((subject) => ({
      subject: subject.subject.length > 12 
        ? subject.subject.substring(0, 9) + '...' 
        : subject.subject,
      student: subject.student_average,
      grade: subject.grade_average,
      fullMark: 100,
    }));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '8px',
          padding: '12px',
          boxShadow: theme.shadows.lg,
        }}>
          <p style={{ margin: 0, fontWeight: 600, color: theme.colors.textPrimary }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '4px 0 0 0', color: entry.color, fontSize: '14px' }}>
              {entry.name}: {entry.value.toFixed(1)}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading comparative data..." />
      </LoadingContainer>
    );
  }

  if (!data || !data.overall_ranking) {
    return (
      <EmptyState>
        <FiAward size={64} />
        <h3>No Comparative Data</h3>
        <p>Comparative analytics will appear once sufficient grade data is available</p>
      </EmptyState>
    );
  }

  const percentileBadge = getPercentileBadge(data.overall_ranking.percentile);

  return (
    <>
      {/* Overall Ranking - Hero Section */}
      <Section>
        <RankingCard>
          <RankBadge color={getPercentileColor(data.overall_ranking.percentile)}>
            <div className="percentile">
              {percentileBadge.emoji}
            </div>
            <div className="suffix">
              {data.overall_ranking.percentile.toFixed(0)}%
            </div>
          </RankBadge>
          
          <RankInfo>
            <h3>
              {data.overall_ranking.performance_level}
            </h3>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>
              Your child is in the <strong>top {(100 - data.overall_ranking.percentile).toFixed(0)}%</strong> of {data.grade}!
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '12px' }}>
              <Badge color={percentileBadge.color}>
                {percentileBadge.label}
              </Badge>
              <Badge color={theme.colors.info}>
                {data.total_students_in_grade} students in grade
              </Badge>
            </div>
          </RankInfo>
        </RankingCard>
      </Section>

      {/* Performance Comparison */}
      <Section>
        <SectionTitle>
          <FiTarget />
          Performance vs. Grade Average
        </SectionTitle>
        <Grid minWidth="250px">
          <StatCard>
            <StatLabel>Student Average</StatLabel>
            <StatValue color={theme.colors.primary}>
              {data.overall_ranking.student_average.toFixed(1)}%
            </StatValue>
            <StatDescription>
              Current performance level
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Grade Average</StatLabel>
            <StatValue color={theme.colors.textSecondary}>
              {data.overall_ranking.grade_average.toFixed(1)}%
            </StatValue>
            <StatDescription>
              {data.grade} average score
            </StatDescription>
          </StatCard>

          <StatCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <StatLabel>Performance Gap</StatLabel>
              <Badge color={data.overall_ranking.difference >= 0 ? theme.colors.success : theme.colors.error}>
                {data.overall_ranking.difference >= 0 ? '+' : ''}{data.overall_ranking.difference.toFixed(1)}%
              </Badge>
            </div>
            <StatValue color={data.overall_ranking.difference >= 0 ? theme.colors.success : theme.colors.error}>
              {Math.abs(data.overall_ranking.difference).toFixed(1)}%
            </StatValue>
            <StatDescription>
              {data.overall_ranking.difference >= 0 ? 'Above' : 'Below'} grade average
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Percentile Rank</StatLabel>
            <StatValue color={getPercentileColor(data.overall_ranking.percentile)}>
              {data.overall_ranking.percentile.toFixed(0)}
            </StatValue>
            <StatDescription>
              Out of 100 students
            </StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {/* Improvement Trend */}
      {data.improvement && (
        <Section>
          <SectionTitle>
            <FiTrendingUp />
            Performance Improvement
          </SectionTitle>
          <Grid minWidth="300px">
            <StatCard>
              <StatLabel>Recent Average</StatLabel>
              <StatValue color={theme.colors.primary}>
                {data.improvement.recent_average.toFixed(1)}%
              </StatValue>
              <StatDescription>
                Latest performance level
              </StatDescription>
            </StatCard>

            <StatCard>
              <StatLabel>Previous Average</StatLabel>
              <StatValue color={theme.colors.textSecondary}>
                {data.improvement.previous_average.toFixed(1)}%
              </StatValue>
              <StatDescription>
                Earlier performance baseline
              </StatDescription>
            </StatCard>

            <StatCard>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <StatLabel>Improvement</StatLabel>
                <Badge color={data.improvement.improvement >= 0 ? theme.colors.success : theme.colors.error}>
                  {data.improvement.trend}
                </Badge>
              </div>
              <StatValue color={data.improvement.improvement >= 0 ? theme.colors.success : theme.colors.error}>
                {data.improvement.improvement >= 0 ? '+' : ''}{data.improvement.improvement.toFixed(1)}%
              </StatValue>
              <StatDescription>
                Performance change over time
              </StatDescription>
            </StatCard>
          </Grid>
        </Section>
      )}

      {/* Subject Rankings */}
      {data.subject_rankings && data.subject_rankings.length > 0 && (
        <>
          <Section>
            <SectionTitle>
              Subject-wise Performance Comparison
            </SectionTitle>
            <Card>
              <p style={{ color: theme.colors.textSecondary, marginBottom: '20px' }}>
                Student performance compared to {data.grade} average by subject
              </p>
              <ChartContainer height="400px" mobileHeight="300px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={prepareSubjectComparison()}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                    <XAxis
                      dataKey="subject"
                      tick={{ fill: theme.colors.textSecondary, fontSize: 12 }}
                      angle={-15}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      tick={{ fill: theme.colors.textSecondary }}
                      domain={[0, 100]}
                      label={{
                        value: 'Score (%)',
                        angle: -90,
                        position: 'insideLeft',
                        fill: theme.colors.textSecondary,
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="studentScore"
                      fill={theme.colors.primary}
                      name="Student Score"
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar
                      dataKey="gradeAverage"
                      fill={theme.colors.textSecondary}
                      name="Grade Average"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </Card>
          </Section>

          {/* Subject Performance Details */}
          <Section>
            <SectionTitle>
              Detailed Subject Comparison
            </SectionTitle>
            <Grid minWidth="300px">
              {data.subject_rankings.map((subject, index) => (
                <Card
                  key={index}
                  style={{
                    borderLeft: `4px solid ${getPerformanceColor(subject.performance)}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: theme.colors.textPrimary }}>
                      {subject.subject}
                    </h3>
                    <Badge color={getPerformanceColor(subject.performance)}>
                      {subject.performance}
                    </Badge>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginBottom: '4px' }}>
                        Student
                      </div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.primary }}>
                        {subject.student_average.toFixed(1)}%
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginBottom: '4px' }}>
                        Grade Avg
                      </div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.textSecondary }}>
                        {subject.grade_average.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                  
                  <div style={{
                    padding: '8px 12px',
                    background: `${getPerformanceColor(subject.performance)}15`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: theme.colors.textPrimary,
                  }}>
                    {subject.difference >= 0 ? '+' : ''}{subject.difference.toFixed(1)}% vs. grade average
                  </div>
                </Card>
              ))}
            </Grid>
          </Section>

          {/* Radar Chart Comparison */}
          {data.subject_rankings.length >= 3 && (
            <Section>
              <SectionTitle>
                Performance Profile Overview
              </SectionTitle>
              <Card>
                <ChartContainer height="450px" mobileHeight="350px">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={prepareRadarData()}>
                      <PolarGrid stroke={theme.colors.border} />
                      <PolarAngleAxis
                        dataKey="subject"
                        tick={{ fill: theme.colors.textSecondary, fontSize: 12 }}
                      />
                      <PolarRadiusAxis
                        angle={90}
                        domain={[0, 100]}
                        tick={{ fill: theme.colors.textSecondary }}
                      />
                      <Radar
                        name="Student"
                        dataKey="student"
                        stroke={theme.colors.primary}
                        fill={theme.colors.primary}
                        fillOpacity={0.6}
                      />
                      <Radar
                        name="Grade Average"
                        dataKey="grade"
                        stroke={theme.colors.textSecondary}
                        fill={theme.colors.textSecondary}
                        fillOpacity={0.3}
                      />
                      <Legend />
                      <Tooltip content={<CustomTooltip />} />
                    </RadarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </Card>
            </Section>
          )}
        </>
      )}

      {/* Badge Ranking */}
      {data.badge_ranking && (
        <Section>
          <SectionTitle>
            <FiAward />
            Badge Achievements
          </SectionTitle>
          <RankingCard>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.primary})`,
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
              flexShrink: 0,
            }}>
              🏆
            </div>
            
            <RankInfo>
              <h3>
                Rank #{data.badge_ranking.rank} in Badge Collection
              </h3>
              <p style={{ fontSize: '16px', marginBottom: '8px' }}>
                Your child has earned <strong>{data.badge_ranking.student_badges} badges</strong>, 
                ranking #{data.badge_ranking.rank} out of {data.badge_ranking.total_students} students!
              </p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '12px' }}>
                <StatCard style={{ flex: 1, minWidth: '150px' }}>
                  <StatLabel>Student Badges</StatLabel>
                  <StatValue color={theme.colors.accent}>
                    {data.badge_ranking.student_badges}
                  </StatValue>
                </StatCard>
                <StatCard style={{ flex: 1, minWidth: '150px' }}>
                  <StatLabel>Grade Average</StatLabel>
                  <StatValue color={theme.colors.textSecondary}>
                    {data.badge_ranking.average_badges.toFixed(1)}
                  </StatValue>
                </StatCard>
              </div>
            </RankInfo>
          </RankingCard>
        </Section>
      )}

      {/* Encouragement Section */}
      <Section>
        <Card
          style={{
            background: `linear-gradient(135deg, ${theme.colors.primary}15, ${theme.colors.secondary}15)`,
            border: `2px solid ${theme.colors.primary}`,
            padding: '32px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>
            {data.overall_ranking.percentile >= 90 ? '🌟' : 
             data.overall_ranking.percentile >= 75 ? '💪' : 
             data.overall_ranking.percentile >= 50 ? '📈' : '🎯'}
          </div>
          <h2 style={{ margin: '0 0 12px 0', color: theme.colors.textPrimary, fontSize: '24px' }}>
            {data.overall_ranking.percentile >= 90 ? 'Outstanding Performance!' : 
             data.overall_ranking.percentile >= 75 ? 'Great Work!' : 
             data.overall_ranking.percentile >= 50 ? 'Keep Improving!' : 'Stay Focused!'}
          </h2>
          <p style={{ margin: 0, fontSize: '16px', color: theme.colors.textSecondary, lineHeight: '1.6' }}>
            {data.overall_ranking.percentile >= 90 ? 
              'Your child is performing exceptionally well, ranking in the top 10% of their grade!' : 
             data.overall_ranking.percentile >= 75 ? 
              'Your child is performing above average and showing great potential!' : 
             data.overall_ranking.percentile >= 50 ? 
              'Your child is making steady progress. Keep encouraging consistent study habits!' : 
              'With focused effort and support, your child can improve their ranking significantly!'}
          </p>
        </Card>
      </Section>
    </>
  );
};

export default ComparativeAnalytics;

